import { InfoOutlined } from "@mui/icons-material";
import { IconButton, type SvgIconProps, type SxProps, Tooltip } from "@mui/material";
import { type ComponentType, type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../../hooks/useMobile";

type Props = {
	disabled?: boolean;
	mobileTooltipDurationMs?: number;
	title: ReactNode;
	iconSx?: SxProps;
	/**
	 * Override the default "ⓘ" glyph with a domain-specific icon (any MUI SvgIcon component) so the
	 * indicator reads as its meaning (e.g. a DST/timelapse marker) while keeping one tooltip primitive.
	 */
	icon?: ComponentType<SvgIconProps>;
	/** Overrides the default `info-icon-tooltip` test id (useful when several are rendered on one row). */
	dataTestId?: string;
};

/**
 * This component renders an information icon with an associated tooltip.
 * For mobile devices, it displays a simple info icon, when clicked shows the tooltip for `mobileTooltipDurationMs` seconds.
 * @constructor
 */
export const InfoIconWithTooltip: FC<Props> = ({
	mobileTooltipDurationMs = 2000,
	disabled = false,
	title,
	iconSx,
	icon: IconComponent = InfoOutlined,
	dataTestId = "info-icon-tooltip",
}) => {
	const [open, setOpen] = useState(false);
	const timeoutRef = useRef<number | null>(null);
	const { isMobile } = useIsMobile();

	const handleClick = () => {
		if (!isMobile) return;

		setOpen(true);

		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = window.setTimeout(() => {
			setOpen(false);
		}, mobileTooltipDurationMs);
	};

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const icon = (
		<IconComponent
			sx={{
				fontSize: "18px",
				cursor: isMobile ? "pointer" : undefined,
				...iconSx,
			}}
		/>
	);

	return (
		<Tooltip
			data-testid={dataTestId}
			title={title}
			open={isMobile ? open : undefined}
			disableHoverListener={isMobile || disabled}
			disableFocusListener={isMobile}
			disableTouchListener={isMobile}
		>
			{isMobile ? <IconButton onClick={handleClick}>{icon}</IconButton> : icon}
		</Tooltip>
	);
};
